"use client";

import type { RegisteredComponent } from "@builder.io/sdk-react";
import dynamic from "next/dynamic";

import { IMAGE_FILE_TYPES } from "@/libs/builder/consts";
import { withValidation } from "@/libs/builder/validators/validateBuilderComponentData";

const showIfVariantWithBackgroundImage = 'options.get("variant") === "withBackgroundImage"';
const showIfShareCodeVariantShareIcons = 'options.get("shareCodeVariant") === "shareIcons"';

export const buildReferralPersonalCodeInputs = () =>
  [
    {
      name: "variant",
      type: "string",
      defaultValue: "default",
      enum: [
        { label: "Default", value: "default" },
        { label: "Dashboard", value: "dashboard" },
        { label: "With background image", value: "withBackgroundImage" },
      ],
    },
    {
      name: "header",
      type: "string",
      friendlyName: "Header above code title",
      showIf: showIfVariantWithBackgroundImage,
    },
    {
      name: "smallText",
      type: "string",
      showIf: showIfVariantWithBackgroundImage,
      friendlyName: "Small bottom text",
    },
    {
      name: "backgroundImage",
      type: "file",
      showIf: showIfVariantWithBackgroundImage,
      allowedFileTypes: IMAGE_FILE_TYPES,
      defaultValue:
        "https://cdn.builder.io/api/v1/image/assets%2F90293f7dc9704cb5935f357315020051%2Fef3c4bcad86e40788fe504759413070b",
    },
    {
      name: "backgroundImageTablet",
      type: "file",
      showIf: showIfVariantWithBackgroundImage,
      allowedFileTypes: IMAGE_FILE_TYPES,
      defaultValue:
        "https://cdn.builder.io/api/v1/image/assets%2F90293f7dc9704cb5935f357315020051%2Fef3c4bcad86e40788fe504759413070b",
    },
    {
      name: "backgroundImageMobile",
      type: "file",
      showIf: showIfVariantWithBackgroundImage,
      allowedFileTypes: IMAGE_FILE_TYPES,
      defaultValue:
        "https://cdn.builder.io/api/v1/image/assets%2F90293f7dc9704cb5935f357315020051%2Fef3c4bcad86e40788fe504759413070b",
    },
    {
      name: "referralPageVariant",
      friendlyName: "Refer a friend page variant",
      type: "boolean",
      defaultValue: false,
      showIf: showIfVariantWithBackgroundImage,
    },
    {
      name: "topHeader",
      friendlyName: "Top header",
      type: "string",
    },
    {
      name: "codeTitle",
      type: "string",
      defaultValue: "Your personal code",
      required: true,
    },
    {
      name: "shareCodeVariant",
      type: "string",
      friendlyName: "Share code variant",
      defaultValue: "shareIcons",
      enum: [
        { label: "Share icons", value: "shareIcons" },
        { label: "Share button", value: "shareButton" },
      ],
    },
    {
      name: "shareTitle",
      defaultValue: "Invite a friend",
      showIf: showIfShareCodeVariantShareIcons,
      type: "string",
      required: true,
    },
    {
      name: "description",
      type: "richText",
    },
    {
      name: "infoTitle",
      type: "string",
      showIf: showIfShareCodeVariantShareIcons,
      defaultValue: "Tap to copy to clipboard",
      friendlyName: "Info title",
    },
    {
      name: "items",
      type: "list",
      friendlyName: "Share icons",
      required: false,
      showIf: showIfShareCodeVariantShareIcons,
      defaultValue: [
        {
          img: "https://cdn.builder.io/api/v1/image/assets%2F90293f7dc9704cb5935f357315020051%2Fef1ae9af4604416fb7ee36a299883fb5",
          url: "#",
          trackingName: "Telegram",
        },
        {
          img: "https://cdn.builder.io/api/v1/image/assets%2F90293f7dc9704cb5935f357315020051%2Fa9ff7d81326a40cba59334078ce44085",
          url: "#",
          trackingName: "Instagram",
        },
        {
          img: "https://cdn.builder.io/api/v1/image/assets%2F90293f7dc9704cb5935f357315020051%2F8b96c3549e6c4cb587d485165e6da3bf",
          url: "#",
          trackingName: "WhatsApp",
        },
        {
          img: "https://cdn.builder.io/api/v1/image/assets%2F90293f7dc9704cb5935f357315020051%2F2663558f64a348a9ac8d5b290d5aa95c",
          url: "mailto:?body=share",
          trackingName: "Mail",
        },
      ],
      subFields: [
        {
          name: "url",
          type: "url",
          friendlyName: "Icon URL",
          required: true,
        },
        {
          name: "img",
          type: "file",
          friendlyName: "Icon image",
          allowedFileTypes: IMAGE_FILE_TYPES,
          required: true,
        },
        {
          name: "trackingName",
          type: "string",
          friendlyName: "Tracking name",
          required: true,
        },
      ],
    },
    {
      name: "shareConfig",
      type: "object",
      showIf: 'options.get("shareCodeVariant") === "shareButton"',
      defaultValue: {
        buttonLabel: "Share",
        shareDialogTitle: "Invite a friend",
        shareDialogDescription: "Invite your friends to join and get rewarded. Spread the word via:",
        shareUrl: "https://example.com/?code={#referralCode#}",
        shareTextPrefix: "Send your friends to sign up with my code:",
        providers: ["whatsapp", "messenger", "telegram", "email", "sms", "kakao", "copy"],
        messenger: {
          appId: "",
          redirectUri: "",
        },
      },
      subFields: [
        {
          name: "buttonLabel",
          type: "string",
          required: false,
        },
        {
          name: "shareDialogTitle",
          defaultValue: "Invite a friend",
          type: "string",
          required: false,
        },
        {
          name: "shareDialogDescription",
          defaultValue: "Invite your friends to join and get rewarded. Spread the word via:",
          type: "string",
          required: false,
        },

        {
          name: "shareUrl",
          type: "url",
          required: false,
        },
        {
          name: "shareTextPrefix",
          type: "string",
        },
        {
          name: "providers",
          type: "Tags",
          helperText: `Possible values: whatsapp, messenger, telegram, viber, email, sms, kakao, copy. If "copy" is not specified it will be added automatically at the very end.`,
          onChange: (options) => {
            const providers = options.get("providers");
            if (Array.isArray(providers)) {
              const allowedShareProvider = [
                "whatsapp",
                "messenger",
                "telegram",
                "viber",
                "email",
                "sms",
                "kakao",
                "copy",
              ] as const;
              options.set(
                "providers",
                providers.filter((item) => allowedShareProvider.includes(item))
              );
            }
          },
        },
        {
          name: "messengerConfig",
          type: "object",
          defaultValue: {
            appId: "",
            redirectUri: "",
          },
          subFields: [
            {
              name: "appId",
              type: "string",
            },
            {
              name: "redirectUri",
              type: "string",
            },
          ],
        },
      ],
    },
    {
      name: "withBottomCta",
      type: "boolean",
      required: false,
    },
    {
      name: "bottomCtaLabel",
      type: "string",
      required: false,
    },
    {
      name: "bottomCtaLink",
      type: "string",
      required: false,
    },
  ] as const satisfies Inputs;

export const referralPersonalCodeInputs = [
  ...buildReferralPersonalCodeInputs(),
  {
    name: "registerUrl",
    type: "url",
    friendlyName: "Sign up button url",
    defaultValue: "#",
    required: false,
  },
  {
    name: "registerTitle",
    type: "string",
    defaultValue: "Sign up",
    friendlyName: "Sign up button label",
    required: false,
  },
  {
    name: "signInUrl",
    type: "url",
    defaultValue: "#",
    friendlyName: "Sign in button url",
    required: false,
  },
  {
    name: "signInTitle",
    type: "string",
    defaultValue: "Sign in",
    friendlyName: "Sign in button label",
    required: false,
  },
] as const satisfies Inputs;

export const ReferralPersonalCodeComponent: RegisteredComponent = {
  name: "ReferralPersonalCode",
  component: dynamic(async () =>
    withValidation(
      referralPersonalCodeInputs,
      (await import("./ReferralPersonalCode")).ReferralPersonalCode,
      "ReferralPersonalCode"
    )
  ),
  inputs: referralPersonalCodeInputs,
  requiresParent: {
    query: {
      "component.name": { $in: ["LoyaltyWrapper"] },
    },
    message: "'ReferralPersonalCode' component can only be added inside a 'LoyaltyWrapper'",
  },
};
